import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    CircularProgress, Button, Box, Typography, Alert,
} from "@mui/material";
import NavBar from "../navbar";
import { validateLoggedIn, getSession } from "../utils";
import { Link, useNavigate } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const Endpoints = () => {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [enriching, setEnriching] = useState(false);

    const fetchData = async () => {
        try {
            const response = await fetch(config.apiBaseUrl + "/admin/endpoints", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
            });
            if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
            setData(await response.json());
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        }
    };

    const handleEnrich = async () => {
        setEnriching(true);
        try {
            const response = await fetch(config.apiBaseUrl + "/endpoints/enrich", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
            });
            if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
            await fetchData();
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setEnriching(false);
        }
    };

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) { setError(validate); return; }
        fetchData();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };


    const sortData = (rows, orderBy, order) => {
        return [...rows].sort((a, b) => {
            const valA = a[orderBy]; const valB = b[orderBy];
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            if (typeof valA === "number" && typeof valB === "number") return order === "asc" ? valA - valB : valB - valA;
            if (typeof valA === "string" && typeof valB === "string") return order === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
            return order === "asc" ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
        });
    };

    if (error) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;
    if (!data) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;

    const sortedRows = data.length > 0 ? sortData(data, orderBy, order) : [];

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" component="h1">Endpoints</Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button variant="outlined" color="secondary" startIcon={<SyncIcon />} onClick={handleEnrich} disabled={enriching}>
                            {enriching ? "Enriching..." : "Enrich"}
                        </Button>
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate("/endpoints/new")}>
                            Create Endpoint
                        </Button>
                    </Box>
                </Box>
                {data.length === 0 ? (
                    <Alert severity="info">No endpoints found.</Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell column="path" label="Path" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="method" label="Method" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="price" label="Price" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="increase_every" label="Increase Every" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <TableCell>Enabled</TableCell>
                                    <TableCell>Removed</TableCell>
                                    <TableCell>Hidden From Docs</TableCell>
                                    <SortableTableCell column="created_at" label="Created At" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedRows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <Link to={`/endpoints/${row.id}/edit`} style={{ textDecoration: "none", color: "#007bff" }}>
                                                {row.path}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{row.method.toUpperCase()}</TableCell>
                                        <TableCell>{row.price}</TableCell>
                                        <TableCell>{row.increase_every !== null ? row.increase_every : "—"}</TableCell>
                                        <TableCell>{row.enabled ? "Yes" : "No"}</TableCell>
                                        <TableCell>{row.removed ? "Yes" : "No"}</TableCell>
                                        <TableCell>{row.hidden_from_docs ? "Yes" : "No"}</TableCell>
                                        <TableCell>{formatUtc(row.created_at)}</TableCell>
                                        <TableCell align="center">
                                            <Button variant="outlined" size="small" onClick={() => navigate(`/endpoints/${row.id}/edit`)}>
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    );
};

const SortableTableCell = ({ column, label, orderBy, order, handleSort }) => (
    <TableCell onClick={() => handleSort(column)} style={{ cursor: "pointer" }}>
        {label} {orderBy === column && (order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
    </TableCell>
);

export default Endpoints;
